import { ApiError, request } from "../api/client";
import { ResultPolicyRequiredError } from "./ResultPolicyRequiredError";
import { operations } from "./contracts";
import type { Context, Operation, RecordValue, Schema, Value } from "./types";
export function operation(name: string, write = false): Operation {
  const found = operations.find(
    (o) => o.operation === name && (o.method !== "GET") === write,
  );
  if (!found) throw new Error("Unknown event operation");
  return found;
}
export function querySchema(op: Operation): Schema {
  return {
    type: "object",
    properties: Object.fromEntries(op.query.map((q) => [q.name, q.schema])),
    required: op.query.filter((q) => q.required).map((q) => q.name),
  };
}
export function queryDefaults(op: Operation, ctx: Context): RecordValue {
  const scope: RecordValue = {
    organizerId: ctx.organizerId,
    serviceProductId: ctx.serviceProductId,
  };
  if (ctx.unit)
    Object.assign(scope, {
      unitId: ctx.unit.unitId,
      parentUnitId: ctx.unit.unitId,
      roundId: ctx.unit.unitId,
      sourceRoundId: ctx.unit.unitId,
      resultUnitId: ctx.unit.unitId,
      registrationScopeId: ctx.unit.registrationScopeId,
    });
  const query = Object.fromEntries(
    op.query.filter((q) => scope[q.name]).map((q) => [q.name, scope[q.name]]),
  );
  if (op.operation === "entries") {
    if (ctx.unit?.unitKind === "VOTING_ROUND") delete query.registrationScopeId;
    else delete query.roundId;
  }
  if (op.operation === "entry") delete query.roundId;
  if (op.operation === "entryCandidates" && ctx.unit?.unitKind !== "VOTING_ROUND") delete query.roundId;
  return query;
}
export async function eventRequest<T = RecordValue>(
  op: Operation,
  query: RecordValue,
  body?: RecordValue,
  signal?: AbortSignal,
): Promise<T> {
  const params = new URLSearchParams();
  for (const q of op.query)
    if (query[q.name] !== undefined && query[q.name] !== "")
      params.set(q.name, String(query[q.name]));
  const started = performance.now();
  const trace = { operation: op.operation, method: op.method, path: op.path };
  if (__MZN_LOCAL_FIXTURES__) console.info("[MZN Use Case] started " + JSON.stringify(trace));
  try {
    const result = await request<T>(
      op.path + (params.size ? "?" + params : ""),
      op.method,
      body,
      signal,
    );
    if (__MZN_LOCAL_FIXTURES__) console.info("[MZN Use Case] succeeded " + JSON.stringify({
      ...trace, durationMs: Math.round(performance.now() - started),
    }));
    return result;
  } catch (error) {
    if (__MZN_LOCAL_FIXTURES__ && !signal?.aborted) console.warn("[MZN Use Case] rejected " + JSON.stringify({
      ...trace, status: error instanceof ApiError ? error.status : 0,
      code: error instanceof ApiError ? error.code : "NETWORK_ERROR",
    }));
    throw error;
  }
}
async function read(name: string, query: RecordValue, signal: AbortSignal) {
  return eventRequest(operation(name), query, undefined, signal);
}
export async function prepare(
  op: Operation,
  query: RecordValue,
  ctx: Context,
  _actorId: string,
  signal: AbortSignal,
): Promise<RecordValue> {
  const name = op.operation;
  if (name === "unitCreate")
    return {
      unitId: crypto.randomUUID(),
      ...(ctx.unit
        ? {
            parentUnitId: ctx.unit.unitId,
            expectedParentVersion: ctx.unit.version,
          }
        : {
            organizerId: ctx.organizerId,
            serviceProductId: ctx.serviceProductId,
          }),
    };
  if (["productPolicy", "workspaceProvision", "audienceGrant"].includes(name)) {
    const kind =
      name === "productPolicy"
        ? "product"
        : name === "workspaceProvision"
          ? "workspace"
          : "audience";
    const parameters = new URLSearchParams({
      kind,
      organizerId: ctx.organizerId,
      serviceProductId: ctx.serviceProductId,
    });
    if (query.individualId)
      parameters.set("individualId", String(query.individualId));
    const state = await request<RecordValue>(
      "/events/setupState?" + parameters,
      "GET",
      undefined,
      signal,
    );
    return {
      ...(state.definition as RecordValue),
      expectedVersion: state.version,
    };
  }
  if (name === "groupCreate") {
    const access = await read(
      "access",
      { organizerId: ctx.organizerId, serviceProductId: ctx.serviceProductId },
      signal,
    );
    return {
      expectedVersion: access.version,
      partyId: crypto.randomUUID(),
      initialMembers: [],
    };
  }
  if (["groupInvitation", "groupMember"].includes(name)) {
    const group = await request<RecordValue>(
      "/events/partyGroup?" +
        new URLSearchParams({ partyId: String(query.partyId) }),
      "GET",
      undefined,
      signal,
    );
    return { expectedVersion: group.version };
  }
  if (
    [
      "entryUpdate",
      "entryRoster",
      "entryMemberPolicy",
      "entryMedia",
      "entryLifecycle",
    ].includes(name)
  ) {
    const scopedRoster = name === "entryRoster" && ctx.unit?.unitKind === "VOTING_ROUND";
    const entry = await read("entry", { entryId: query.entryId, ...(scopedRoster ? { roundId: ctx.unit!.unitId } : {}) }, signal);
    const round = scopedRoster ? await read("unit", { unitId: ctx.unit!.unitId }, signal) : null;
    return {
      ...entry,
      ...((entry.memberPolicy as RecordValue) || {}),
      members: entry.roster || [],
      expectedVersion: entry.version,
      ...(round ? { _roundRosterFrozen: round.status === "PUBLISHED" || typeof round.roundState === "string" && round.roundState !== "DRAFT" } : {}),
    };
  }
  const key =
    name === "entryCreate"
      ? "registrationScopeId"
      : name === "entryPromote"
        ? "sourceRoundId"
        : name === "unitOrder"
          ? "parentUnitId"
          : name === "resultPolicy"
            ? "resultUnitId"
            : [
                  "votingPolicy",
                  "evaluationForm",
                  "roundSchedule",
                  "roundLifecycle",
                  "roundPresentation",
                  "roundEntry",
                ].includes(name)
              ? "roundId"
              : "unitId";
  const unit = await read("unit", { unitId: query[key] }, signal);
  const body: RecordValue = { expectedVersion: unit.version };
  if (name === "unitUpdate") {
    Object.assign(body, unit);
    if (unit.unitKind !== "VOTING_ROUND" || unit.roundWeightBps == null) delete body.roundWeightBps;
  }
  if (name === "entryCreate") body.entryId = crypto.randomUUID();
  if (name === "unitOrder") {
    const siblings: Value[] = [];
    let cursor = "";
    do {
      const page = await read(
        "units",
        {
          parentUnitId: query.parentUnitId,
          limit: 100,
          ...(cursor ? { cursor } : {}),
        },
        signal,
      );
      siblings.push(...(page.items as RecordValue[]).map((u) => u.unitId));
      cursor = String(page.nextCursor || "");
      if (siblings.length >= 1000 && cursor)
        throw new Error("Ordering exceeds the configured capacity");
    } while (cursor);
    body.orderedUnitIds = siblings;
  }
  if (["votingPolicy", "resultPolicy", "evaluationForm"].includes(name)) {
    try {
      const saved = await read(name, query, signal);
      Object.assign(body, saved.policy || saved.definition || {});
    } catch (error) {
      if (
        !(
          name === "evaluationForm" &&
          error instanceof ApiError &&
          error.status === 404
        )
      )
        throw error;
    }
  }
  if (name === "votingPolicy" && body.maxSelections === undefined) body.maxSelections = 1;
  if (["roundSchedule", "roundPresentation"].includes(name)) {
    const live = await read("roundLive", { roundId: query.roundId }, signal);
    Object.assign(
      body,
      live[name === "roundSchedule" ? "schedule" : "presentation"] || {},
    );
    if (name === "roundSchedule" && !body.timezone)
      body.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  if (name === "unitMove") {
    const target = await read(
      "unit",
      { unitId: query.targetParentUnitId },
      signal,
    );
    body.expectedTargetParentVersion = target.version;
  }
  if (name === "entryPromote") {
    const target = await read("unit", { unitId: query.targetRoundId }, signal);
    body.expectedTargetVersion = target.version;
  }
  if (name === "roundEntry") {
    const group = await request<RecordValue>("/events/partyGroup?" + new URLSearchParams({ partyId: String(query.partyId) }), "GET", undefined, signal);
    const scope = await read("unit", { unitId: unit.registrationScopeId }, signal);
    body.expectedGroupVersion = group.version;
    body.expectedScopeVersion = scope.version;
  }
  if (name === "resultCompute") {
    const policy = await read(
      "resultPolicy",
      { resultUnitId: query.unitId },
      signal,
    );
    if (!policy.configVersion) throw new ResultPolicyRequiredError();
    body.expectedConfigVersion = policy.configVersion;
  }
  if (name === "resultPublish") {
    const result = await read("result", { unitId: query.unitId, snapshotId: query.snapshotId, limit: 1 }, signal);
    if (result.unitId !== query.unitId || result.snapshotId !== query.snapshotId ||
        !["MANAGERS", "PARTICIPANTS", "PUBLIC"].includes(String(result.visibility)))
      throw new Error("Result publication visibility is unavailable");
    body.visibility = result.visibility;
  }
  return body;
}
export function boundFields(op: Operation, ctx: Context): string[] {
  return [
    "organizerId",
    "serviceProductId",
    ...(ctx.unit
      ? [
          "unitId",
          "parentUnitId",
          "registrationScopeId",
          "roundId",
          "sourceRoundId",
          "resultUnitId",
        ]
      : []),
  ].filter((k) => op.query.some((q) => q.name === k));
}
export function scalar(value: Value): string {
  return value === null ? "—" : String(value);
}
